// components/ConfirmModal.tsx — branded confirm dialog (P6 polish #2).
//
// Replaces native window.confirm/alert so confirmations match the dark/light
// theme and are properly accessible. Controlled component: render it with
// `open` and a description of the action; the parent owns the pending-action
// state. role=dialog + aria-modal, focus moves to the primary button on open,
// Esc + backdrop-click cancel, 44px tap targets.

import type { JSX } from 'react';
import { useEffect, useId, useRef } from 'react';
import { useTokens } from '../state/theme';

export interface ConfirmModalProps {
  open: boolean;
  title: string;
  body?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Render the confirm button in the rose "destructive" tone. */
  destructive?: boolean;
  /** Optional async state; omitted consumers retain the original behavior. */
  busy?: boolean;
  status?: string;
  error?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmModal({
  open,
  title,
  body,
  confirmLabel = 'Conferma',
  cancelLabel = 'Annulla',
  destructive = false,
  busy = false,
  status,
  error,
  onConfirm,
  onCancel,
}: ConfirmModalProps): JSX.Element | null {
  const confirmRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);
  const previousErrorRef = useRef<string | undefined>(undefined);
  const instanceId = useId().replace(/:/g, '');
  const headingId = `confirm-modal-${instanceId}-title`;
  const bodyId = `confirm-modal-${instanceId}-body`;
  const statusId = `confirm-modal-${instanceId}-status`;
  const errorId = `confirm-modal-${instanceId}-error`;
  const describedBy = [body ? bodyId : null, status ? statusId : null, error ? errorId : null]
    .filter(Boolean)
    .join(' ') || undefined;

  useEffect(() => {
    const wasOpen = wasOpenRef.current;
    if (open && !wasOpen) {
      returnFocusRef.current = document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
      confirmRef.current?.focus();
    } else if (!open && wasOpen) {
      const trigger = returnFocusRef.current;
      returnFocusRef.current = null;
      if (trigger?.isConnected) trigger.focus();
    }
    wasOpenRef.current = open;
  }, [open]);

  useEffect(() => {
    if (!open) {
      previousErrorRef.current = undefined;
      return;
    }
    if (error && error !== previousErrorRef.current) confirmRef.current?.focus();
    previousErrorRef.current = error;
  }, [error, open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        if (!busy) onCancel();
        return;
      }
      if (event.key !== 'Tab') return;

      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(
        'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])',
      ));
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !dialog.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !dialog.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onCancel, open]);

  const tokens = useTokens();
  if (!open) return null;

  const confirmBg = destructive ? tokens.wrong : tokens.accent;
  const confirmFg = tokens.mode === 'dark' ? '#1a1510' : '#fff';

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={headingId}
      aria-describedby={describedBy}
      aria-busy={busy || undefined}
      data-testid="confirm-modal"
      onClick={() => { if (!busy) onCancel(); }}
      style={{
        position:       'fixed',
        inset:          0,
        background:     'rgba(0,0,0,0.55)',
        display:        'flex',
        alignItems:     'center',
        justifyContent: 'center',
        padding:        20,
        zIndex:         1100,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background:    tokens.bg,
          color:         tokens.text,
          border:        `1px solid ${tokens.line}`,
          borderRadius:  tokens.radius.lg,
          padding:       '22px 20px 18px',
          maxWidth:      400,
          width:         '100%',
          boxSizing:     'border-box',
          boxShadow:     '0 10px 40px rgba(0,0,0,0.35)',
        }}
      >
        <h2
          id={headingId}
          style={{
            fontFamily:    tokens.fonts.heading,
            fontSize:      18,
            fontWeight:    700,
            color:         tokens.text,
            margin:        '0 0 8px',
            letterSpacing: -0.2,
            lineHeight:    1.3,
          }}
        >
          {title}
        </h2>
        {body && (
          <p id={bodyId} style={{ fontFamily: tokens.fonts.body, fontSize: 14, color: tokens.textMuted, margin: '0 0 18px', lineHeight: 1.5 }}>
            {body}
          </p>
        )}
        {status && (
          <p id={statusId} role="status" style={{ fontFamily: tokens.fonts.body, fontSize: 14, color: tokens.textMuted, margin: '0 0 14px', lineHeight: 1.5 }}>
            {status}
          </p>
        )}
        {error && (
          <p id={errorId} role="alert" style={{ fontFamily: tokens.fonts.body, fontSize: 14, color: tokens.wrongText, margin: '0 0 14px', lineHeight: 1.5 }}>
            {error}
          </p>
        )}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button
            type="button"
            disabled={busy}
            onClick={() => { if (!busy) onCancel(); }}
            style={{
              minHeight:    44,
              background:   'transparent',
              border:       `1px solid ${tokens.lineStrong}`,
              borderRadius: tokens.radius.pill,
              padding:      '10px 18px',
              color:        tokens.text,
              fontFamily:   tokens.fonts.body,
              fontSize:     14,
              fontWeight:   600,
              cursor:       busy ? 'wait' : 'pointer',
              opacity:      busy ? 0.6 : 1,
            }}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            ref={confirmRef}
            disabled={busy}
            onClick={() => { if (!busy) onConfirm(); }}
            data-testid="confirm-modal-confirm"
            style={{
              minHeight:    44,
              background:   confirmBg,
              border:       'none',
              borderRadius: tokens.radius.pill,
              padding:      '10px 20px',
              color:        confirmFg,
              fontFamily:   tokens.fonts.body,
              fontSize:     14,
              fontWeight:   700,
              cursor:       busy ? 'wait' : 'pointer',
              opacity:      busy ? 0.7 : 1,
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
